/** @jsxImportSource @emotion/react */
import { useCallback, useState } from 'react';
import { IconButton } from '@mui/material';
import { useAtomCallback } from 'jotai/utils';
import { css } from '@emotion/react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  toggleSelectedChatIdsAtom,
  selectedChatIdsListAtom,
  isAllChatsSelectedAtom,
  excludedChatIdsAtom,
  chatListAtom,
} from '../../../states/chat-list';
import { chatListFamily, updateChatCountAtom } from '../../../states/chat';
import { markMultipleChatsDone } from '../../../requests/mark-done';
import { useBulkActionOnChats } from '../../../hooks/use-bulk-action-on-chats';
import useNotifications from '../../../../../../shared/hooks/use-notifications';
import { customFieldsAtom } from '../../../states/custom-fields';
import { isEmbedAtom } from '../../../../../embed/state';
import useEmbedVisibilitySettings from '../../../../../embed/hooks/use-embed-visibility-settings';
import MessageCheckIcon from '../../../../../../shared/icons/icons-revamp/message_check';
import MultiSelectChatDropdown from '../../multi-select-chat-dropdown';
import { ChatType } from '../chat-row/typings';
import { ChatStatuses } from '../../../typings';
import { useSelectedChatsIncludeTypes } from '../../../hooks/use-selected-chats-types';
import QSTooltip from '../../../../../../shared/components/molecules/tooltip';
import { BulkChatAction } from '../../../typings/bulk-action-progress';

const iconButtonCss = css`
  padding: 8px;
`;

/**
 * Shared mark-chat-done icon and 3-dots menu (MultiSelectChatDropdown).
 * Used in both ChatHeaderMain and Searchbar when search is active.
 */
const BulkActionIcons = () => {
  const { addNotification } = useNotifications();
  const isEmbed = useAtomValue(isEmbedAtom);
  const { showBulkMarkAsDone } = useEmbedVisibilitySettings();
  const [toggleSelectedChatIds, setToggleSelectedChatIds] = useAtom(toggleSelectedChatIdsAtom);
  const [selectedChatIdsList, setSelectedChatIdsList] = useAtom(selectedChatIdsListAtom);
  const [isAllChatsSelected, setIsAllChatsSelected] = useAtom(isAllChatsSelectedAtom);
  const [excludedChatIds, setExcludedChatIds] = useAtom(excludedChatIdsAtom);
  const chatFilters = useAtomValue(chatListAtom);
  const { data: customFieldsData } = useAtomValue(customFieldsAtom);
  const bulkAction = useBulkActionOnChats();
  const isClosedFilter = chatFilters.status === ChatStatuses.CLOSED || chatFilters.status === ChatStatuses.DONE;
  const selectedChatsIncludeGroup = useSelectedChatsIncludeTypes([ChatType.GROUP]);
  const setUpdateChatCount = useSetAtom(updateChatCountAtom);
  const [selectedUserId, setSelectedUserId] = useState<string>('');

  const selectedChatsDone = useAtomCallback(
    useCallback(
      (get: (atom: ReturnType<typeof chatListFamily>) => unknown) => {
        return selectedChatIdsList.some((chatID: string) => {
          const row = get(chatListFamily({ id: chatID })) as { isDone?: boolean } | null | undefined;
          return !row?.isDone;
        });
      },
      [selectedChatIdsList]
    )
  );

  const handleBulkMarkAsDone = useCallback(async () => {
    if (!isAllChatsSelected && selectedChatIdsList.length === 0) return;
    try {
      if (isAllChatsSelected) {
        await bulkAction({
          filter: chatFilters,
          excludedChatIds,
          action: BulkChatAction.MARK_AS_DONE,
          actionParams: { isDone: true },
          customFieldsData: customFieldsData ?? undefined,
        });
      } else {
        const success = await markMultipleChatsDone({
          chatIds: selectedChatIdsList,
          isDone: true,
        });
        if (!success) {
          addNotification({
            type: 'error',
            message: `Failed to mark ${selectedChatIdsList.length > 1 ? 'chats' : 'chat'} as done.`,
            autoHideDuration: 4000,
          });
          return;
        }
        addNotification({
          type: 'success',
          message: `${selectedChatIdsList.length > 1 ? 'Chats' : 'Chat'} marked as done successfully.`,
          autoHideDuration: 4000,
        });
      }
      setUpdateChatCount((prev: boolean) => !prev);
    } catch (error) {
      addNotification({
        type: 'error',
        message: 'An unexpected error occurred.',
        autoHideDuration: 4000,
      });
    } finally {
      setSelectedChatIdsList([]);
      setToggleSelectedChatIds(false);
      setIsAllChatsSelected(false);
      setExcludedChatIds([]);
    }
  }, [
    selectedChatIdsList,
    isAllChatsSelected,
    excludedChatIds,
    chatFilters,
    customFieldsData,
    setUpdateChatCount,
    addNotification,
    setToggleSelectedChatIds,
    setSelectedChatIdsList,
    setIsAllChatsSelected,
    setExcludedChatIds,
  ]);

  const showMarkDone =
    (!isEmbed || showBulkMarkAsDone) &&
    toggleSelectedChatIds &&
    (selectedChatIdsList.length > 0 || isAllChatsSelected) &&
    !selectedChatsIncludeGroup() &&
    !isClosedFilter &&
    (isAllChatsSelected || selectedChatsDone());

  const showDropdown = toggleSelectedChatIds && (selectedChatIdsList.length > 0 || isAllChatsSelected);

  return (
    <>
      {showMarkDone && (
        <QSTooltip title="Mark as done" placement="top">
          <IconButton css={iconButtonCss} onClick={handleBulkMarkAsDone}>
            <MessageCheckIcon color="#2d2d2d" size={20} />
          </IconButton>
        </QSTooltip>
      )}
      {showDropdown && (
        <MultiSelectChatDropdown selectedUserId={selectedUserId} setSelectedUserId={setSelectedUserId} />
      )}
    </>
  );
};

export default BulkActionIcons;
